import { t, Trans } from "@lingui/macro";
import { bindHover, bindPopover, usePopupState } from "material-ui-popup-state/hooks";
import Popover from "material-ui-popup-state/HoverPopover";
import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { HighProteinInfoPage, KetoInfoPage, ModerateLiberalInfoPage, RecipesFaqPageSchema } from "../../pages";
import { NutritionData } from "../../types/MealPlan";
import { isEmptyNutritionData } from "../../utils/nutrition";
import { Link } from "../Link/Link";
import NutritionBadge from "./NutritionBadge";

const NutritionPopover = styled(Popover)`
  .MuiPopover-paper {
    position: relative;
    max-width: 337px;
    padding: 15px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

const NutritionPopoverTitle = styled.span`
  font-size: 18px;
  font-weight: 600;
`;

const NutritionDisclaimer = styled.div`
  font-size: 12px;
  line-height: 16px;
  margin: 10px 0 5px;
  opacity: 0.5;
`;
function useTranslations() {
  return {
    ketogenicLowCarbLabel: t({
      id: "Nutrition.ketogenicLowCarbLabel",
      message: "Ketogenic low carb",
    }),
    moderateLowCarbLabel: t({
      id: "Nutrition.moderateLowCarbLabel",
      message: "Moderate low carb",
    }),
    liberalLowCarbLabel: t({
      id: "Nutrition.liberalLowCarbLabel",
      message: "Liberal low carb",
    }),
    highProteinKetoLabel: t({
      id: "Nutrition.highProteinKetoLabel",
      message: "High-protein keto ",
    }),
    highProteinLowCarbLabel: t({
      id: "Nutrition.highProteinLowCarbLabel",
      message: "High-protein low carb ",
    }),

    highProteinSubtitle: t({
      id: "Nutrition.highProteinSubtitle",
      message: "Optimized macros for effective and healthy weight loss",
    }),
    ketoSubtitle: t({
      id: "Nutrition.ketoSubtitle",
      message: "Below 4 E% carbs or, 7 g of carbs or less if it is a meal",
    }),
    moderateSubtitle: t({
      id: "Nutrition.moderateSubtitle",
      message: "4-10 E% carbs",
    }),
    liberalSubtitle: t({
      id: "Nutrition.liberalSubtitle",
      message: "10-20 E% carbs",
    }),
    averagePerDayMessage: t({
      id: "Nutrition.averagePerDay",
      message: "Average per day",
    }),
    readMore: t({ id: "Nutrion.readMore", message: "Read more" }),

    rows: {
      netCarbs: t({
        id: "Nutrition.rows.netCarbs",
        message: "Net carbs",
      }),
      fiber: t({
        id: "Nutrition.rows.fiber",
        message: "Fiber",
      }),
      fat: t({
        id: "Nutrition.rows.fat",
        message: "Fat",
      }),
      protein: t({
        id: "Nutrition.rows.protein",
        message: "Protein",
      }),
      kcal: t({
        id: "Nutrition.rows.kcal",
        message: "kcal",
        comment: "Calories in kcal.",
      }),
    },

    nutritionalDisclaimerMessage: (
      <Trans id="Nutrition.nutritionalDisclaimerMessage">
        Nutritional information based on the USDA National Nutrient Database.
      </Trans>
    ),
  };
}

type Translations = ReturnType<typeof useTranslations>;

function useEnergyRating(energyRating: string, tt: Translations, isHighProtein?: boolean) {
  if (isHighProtein) {
    switch (energyRating) {
      case "strict":
      case "keto":
        return tt.highProteinKetoLabel;
      default:
        return tt.highProteinLowCarbLabel;
    }
  } else {
    switch (energyRating) {
      case "strict":
      case "keto":
        return tt.ketogenicLowCarbLabel;
      case "moderate":
        return tt.moderateLowCarbLabel;
      case "liberal":
      default:
        return tt.liberalLowCarbLabel;
    }
  }
}

const getSubtitle = (energyRating: string, tt: Translations, isHighProtein?: boolean) => {
  if (isHighProtein) {
    return tt.highProteinSubtitle;
  } else {
    switch (energyRating) {
      case "strict":
      case "keto":
        return tt.ketoSubtitle;
      case "moderate":
        return tt.moderateSubtitle;
      case "liberal":
      default:
        return tt.liberalSubtitle;
    }
  }
};

const getReadMoreLink = (energyRating: string, isHighProtein?: boolean) => {
  if (isHighProtein) {
    return HighProteinInfoPage;
  } else {
    switch (energyRating) {
      case "strict":
      case "keto":
        return KetoInfoPage;
      default:
        return ModerateLiberalInfoPage;
    }
  }
};

export const NutritionContent = ({
  nutrition,
  strictness,
  isHighProtein,
}: {
  nutrition: NutritionData;
  strictness: string;
  isHighProtein?: boolean;
}) => {
  const tt = useTranslations();
  const energyRating = useEnergyRating(strictness, tt, isHighProtein);
  const subtitle = getSubtitle(strictness, tt, isHighProtein);
  const readMoreLink = getReadMoreLink(strictness, isHighProtein);
  const roundedKcal = Math.round(nutrition.values.calories);

  const nutritionData = [
    {
      value: nutrition.values?.carbs ?? 0,
      percentage: nutrition.percentages?.carbs ?? 0,
      label: tt.rows.netCarbs,
      type: "carbs",
      strictness: strictness,
      totalCarbs: nutrition.values?.totalCarbs,
    },
    {
      value: nutrition.values?.protein ?? 0,
      percentage: nutrition.percentages?.protein ?? 0,
      label: tt.rows.protein,
      type: "protein",
      isHighProtein: isHighProtein,
    },
    {
      value: nutrition.values?.fat ?? 0,
      percentage: nutrition.percentages?.fat ?? 0,
      label: tt.rows.fat,
      type: "fat",
    },
  ];

  return (
    <>
      <NutritionPopoverTitle>{energyRating}</NutritionPopoverTitle>
      <span className="text-xs">{subtitle}</span>
      <Link to={readMoreLink} className="text-xs">
        {tt.readMore}
      </Link>

      <div className="flex flex-row justify-between py-3">
        {nutritionData.map((item) => {
          return (
            <NutritionBadge
              key={item.type}
              value={item.value}
              percentage={item.percentage}
              label={item.label}
              type={item.type}
              strictness={item.strictness}
              isHighProtein={item.isHighProtein}
              totalCarbs={item.totalCarbs}
            />
          );
        })}
      </div>
      <span className="text-sm text-grey font-medium">(kcal: {roundedKcal})</span>
      <Link to={RecipesFaqPageSchema} query={{ question: 10 }}>
        <NutritionDisclaimer>{tt.nutritionalDisclaimerMessage}</NutritionDisclaimer>
      </Link>
    </>
  );
};

const Nutrition = ({
  id,
  nutrition,
  strictness,
  children,
  className,
  isHighProtein,
}: {
  id: string;
  nutrition: NutritionData;
  strictness: string;
  children: ReactNode;
  className?: string;
  isHighProtein?: boolean;
}) => {
  if (isEmptyNutritionData(nutrition)) {
    return null;
  }
  const popupState = usePopupState({
    variant: "popover",
    popupId: id,
  });
  return (
    <div data-testid="nutrition" className={className}>
      <div {...bindHover(popupState)}>{children}</div>
      <NutritionPopover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableRestoreFocus={true}
      >
        <NutritionContent isHighProtein={isHighProtein} nutrition={nutrition} strictness={strictness} />
      </NutritionPopover>
    </div>
  );
};

export default Nutrition;
